import { DocumentDuplicateIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageNavBar } from 'components/PageNavBar'
import { IVisibleLogic } from 'config'
import { accountTypes } from 'config/user.type.constants'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  getFirstVisibleLoanType,
  getLoanTypeMapForSelect,
  LoanType,
  loanTypeMap,
} from 'reducers/loanDetail.reducer'
import { getAdminConfig, setAdminConfig } from 'services'
import { ButtonGroup, InputVisibleLogic, Toggle, ToggleButton, Tooltip } from 'stories/components'
import { capitalizeLettersSeparatedBySlash, confirm, prompt } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'
import { RenderInput } from 'utils/RenderInput'

import { workflowRestrictions } from './constants'
import { IWorkflowConfig, TMultiWorkflowConfig, TWorkflowConfig } from './type'
import { WorkflowAiPromptsTable } from './WorkflowAiPromptsTable'

export const SingleWorkflow = ({
  type,
  data: _data,
  setData,
  setLoading,
}: {
  type: LoanType
  data: TMultiWorkflowConfig
  setData: Function
  setLoading: Function
}) => {
  const data = _data[type]
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const [isChanged, setChanged] = useState(false)

  const onChange = async (
    titleKey: string,
    key: string,
    subKey: keyof IWorkflowConfig,
    value: any,
    needSave = true,
  ) => {
    const temp = cloneDeep(data)
    const group = temp[titleKey] as Record<string, IWorkflowConfig>
    ;(group[key] as any)[subKey] = value
    if (subKey == 'enabled' && Object.keys(group[key]).includes('isActive')) group[key]['isActive'] = value

    setData(temp)

    if (needSave) onSave(temp)
    else setChanged(true)
  }

  const onSave = async (_data: any | null = null) => {
    if (!_data && !isChanged) return

    setLoading(true)
    await setAdminConfig(`workflows/${type}`, _data || data)
    setLoading(false)
    setChanged(false)
  }

  const onCopyFromChannel = async () => {
    const visibleChannels = getLoanTypeMapForSelect()
    const options = Object.keys(_data)
      .filter((key) => key != (type as any) && visibleChannels[key] !== undefined)
      .map((key) => ({ name: visibleChannels[key] || loanTypeMap[key], value: Number(key) }))

    const sourceType = (await confirmOptions('Choose channel to copy from', options)) as any as LoanType
    if ((sourceType as any) === false) return

    const sourceName = loanTypeMap[String(sourceType)]
    const targetName = loanTypeMap[String(type)]

    const content = (
      <div className="text-gray-600 mb-4 text-md">
        Are you sure you want to copy workflow from <strong>{sourceName}</strong> to <strong>{targetName}</strong>?
      </div>
    )
    const confirmed = await confirm(content)
    if (!confirmed) return

    setData(_data[sourceType])
    onSave(_data[sourceType])
  }

  const onChangeTitle = async (titleKey: string, key: string) => {
    const group = data[titleKey] as Record<string, IWorkflowConfig>
    const originalTitle = group[key].title || capitalizeLettersSeparatedBySlash(key)
    const newValue = await prompt(`Input title for Action - ${capitalizeLettersSeparatedBySlash(key)}`, {
      value: originalTitle,
    })
    if (!newValue) return
    onChange(titleKey, key, 'title', newValue)
  }

  return (
    <>
      {Object.keys(data)
        .filter((titleKey) => titleKey !== 'documents' && titleKey !== 'commitmentLetterFields')
        .map((titleKey) => {
          const item = data[titleKey] as Record<string, IWorkflowConfig>
          if (!item || typeof item !== 'object' || Array.isArray(item)) return null
          const defaultItem = _data[type]?.[titleKey] as Record<string, IWorkflowConfig> | undefined
          return (
            <React.Fragment key={titleKey}>
              <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center mb-8`}>
                - {capitalizeLettersSeparatedBySlash(titleKey)}
                <Tooltip message="Copy from other channel">
                  <span className="btn-icon" onClick={() => onCopyFromChannel()}>
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
              <PageNavBar
                titles={Object.keys(item).map(
                  (key) => defaultItem?.[key]?.title || capitalizeLettersSeparatedBySlash(key),
                )}
                activeIndex={-1}
                activeIndexs={Object.keys(item)
                  .map((key, index: number) => {
                    return { key, index }
                  })
                  .filter(({ key }) => item[key].enabled)
                  .map(({ index }) => index)}
                onMove={(index: number) => {
                  const key = Object.keys(item)[index]
                  onChange(titleKey, key, 'enabled', !item[key].enabled)
                }}
                isAllClickable
              />
              {Object.keys(item).map((key) => (
                <div className="mb-4" key={key}>
                  <RenderInput
                    Key={key}
                    key={key}
                    input={{
                      inputType: 'section',
                      title: defaultItem?.[key]?.title || capitalizeLettersSeparatedBySlash(key),
                      additionalElements: (
                        <div className="flex gap-2 items-center">
                          <Toggle
                            id={key}
                            value={item[key].enabled}
                            onChange={(value) => onChange(titleKey, key, 'enabled', value)}
                          />

                          <span className="btn-icon" onClick={() => onChangeTitle(titleKey, key)}>
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>
                        </div>
                      ),
                    }}
                    onChange={() => {}}
                  />
                  <>
                    <div className="flex flex-wrap gap-2 items-center justify-between mt-4 py-2">
                      <div className="max-w-[400px] w-full">
                        <RenderInput
                          Key={key}
                          key={key}
                          input={{
                            inputType: 'multiselect',
                            title: 'Visible For',
                            options: { self_creator: '[Loan] Creator', ...accountTypes },
                            value: item[key].visibleAccountTypes || [],
                            defaultSelected: false,
                          }}
                          onChange={(key: string, value: any) =>
                            onChange(titleKey, key, 'visibleAccountTypes', value, false)
                          }
                          onBlur={() => onSave()}
                        />
                      </div>
                      <div className="max-w-[400px] w-full">
                        <RenderInput
                          Key={key}
                          key={key}
                          input={{
                            inputType: 'multiselect',
                            title: 'Disabled At',
                            options: loanGlobalStatus,
                            value: item[key].disabledAtStatus || [],
                            sort: true,
                            defaultSelected: false,
                          }}
                          onChange={(key: string, value: any) =>
                            onChange(titleKey, key, 'disabledAtStatus', value, false)
                          }
                          onBlur={() => onSave()}
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <p>Active Condition</p>
                        <ToggleButton
                          id={`${key}-isActive`}
                          value={item[key]['isActive']}
                          onChange={(value) => onChange(titleKey, key, 'isActive', value)}
                          disabled={!item[key]['enabled']}
                        />
                      </div>
                    </div>

                    <div>
                      <RenderInput
                        Key={key}
                        key={key}
                        input={{
                          inputType: 'multiselect',
                          title: 'Conditions',
                          options: workflowRestrictions,
                          value: item[key].restrictions || [],
                          defaultSelected: false,
                          prefix: 'alphabet',
                        }}
                        onChange={(key: string, value: any) => onChange(titleKey, key, 'restrictions', value, false)}
                        onBlur={() => onSave()}
                      />
                    </div>
                    <div className="mt-4 bg-slate-50 p-3 rounded-md">
                      <InputVisibleLogic
                        id={`${key}-visible-logic`}
                        title="Visible Conditions"
                        value={item[key].visibleLogic || []}
                        options={{ hasNotVisible: true, hasCustomOrigin: true, hasOtherOrigin: true }}
                        onChange={(logic: IVisibleLogic[][]) => onChange(titleKey, key, 'visibleLogic', logic, true)}
                      />
                    </div>
                    {titleKey === 'loan_submission' && (
                      <WorkflowAiPromptsTable
                        aiPrompts={item[key].aiPrompts || []}
                        onChange={(prompts) => onChange(titleKey, key, 'aiPrompts', prompts, true)}
                      />
                    )}
                  </>
                </div>
              ))}
            </React.Fragment>
          )
        })}
    </>
  )
}

export const Workflows = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<TMultiWorkflowConfig>()
  const [loanType, setLoanType] = useState(getFirstVisibleLoanType)
  const channelTabs = getLoanTypeMapForSelect()

  const initData = async () => {
    setLoading(true)
    const res = await getAdminConfig('workflows')
    setData(res)
    setLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  useEffect(() => {
    if (getLoanTypeMapForSelect()[String(loanType)] === undefined) setLoanType(getFirstVisibleLoanType())
  }, [loanType])

  const onUpdate = (item: TWorkflowConfig) => {
    if (!data) return

    const newData = cloneDeep(data)
    newData[loanType] = item
    setData(newData)
  }

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />

      <div className="text-xl font-bold mb-4">2. Workflows</div>
      <ButtonGroup title={channelTabs} onChange={setLoanType} value={`${loanType}`} />
      {data && <SingleWorkflow type={loanType} data={data} setData={onUpdate} setLoading={setLoading} />}
    </div>
  )
}
